import { useMemo } from 'react';
import { useStudentsList } from '../../../api/hooks';
import type { AlertInstance, StudentListRow } from '../../../api/types';
import { gradeSectionLabel } from './format';

/**
 * Students keyed by id, from the students list the page already may hold. Used to put a grade
 * and section next to an alert while the alert payload does not carry them itself.
 */
export function useStudentLookup(enabled = true) {
  const q = useStudentsList({}, { enabled });
  const rows = q.data?.results;
  return useMemo(() => new Map<number, StudentListRow>((rows ?? []).map((r) => [r.id, r])), [rows]);
}

/** "G10 · B" (or "Grade 10 · B") for an alert: its own `student_grade` / `student_section` first, then the students list. */
export function alertPlace(a: AlertInstance, lookup: Map<number, StudentListRow>, compact = true): string {
  if (a.student_grade !== undefined || a.student_section !== undefined) return gradeSectionLabel(a.student_grade, a.student_section, compact);
  const s = lookup.get(a.student_id);
  return s ? gradeSectionLabel(s.grade_name, s.section, compact) : '';
}

/** Risk score for an alert: its own `student_risk_score` first, then the students list. */
export function alertRisk(a: AlertInstance, lookup: Map<number, StudentListRow>): number | null {
  if (a.student_risk_score !== undefined) return a.student_risk_score;
  return lookup.get(a.student_id)?.risk_score ?? null;
}
